import styled from '@emotion/styled';
import { isActionKey } from 'ui_system/utilities/KeyboardHelpers';
import { colors, fontSizes, brandNeutralColors } from '@anyroad/theme';

const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface RootUnit {
  noun_singular: string;
  noun_plural: string;
}

interface CollapsibleHeaderProps {
  tourNameTranslation: string;
  date: string;
  time: string;
  numberOfGuests: number;
  rootUnit: RootUnit;
  totalFormatted?: string;
  isExpanded: boolean;
  onToggle: () => void;
  tourPicture: string;
}

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px;
  background: ${brandNeutralColors.background.light};
  cursor: pointer;
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    display: none;
  }
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TourImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ContentSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const TourName = styled.h3`
  font-size: ${fontSizes.medium};
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BookingDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${fontSizes.medium};
  color: ${colors.text.secondary};
  flex-wrap: wrap;
`;

const Separator = styled.span`
  color: ${colors.border};
  ::after {
    content: '|';
    margin: 0 4px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
`;

const Price = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: 600;
  color: ${colors.text.primary};
`;

const ToggleIcon = styled.span<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${fontSizes.medium};
  margin-left: 8px;
  color: ${colors.text.secondary};
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isExpanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const CollapsibleHeader = ({
  tourNameTranslation,
  date,
  time,
  numberOfGuests,
  rootUnit,
  totalFormatted,
  isExpanded,
  onToggle,
  tourPicture,
}: CollapsibleHeaderProps) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isActionKey(event)) {
      event.preventDefault();
      onToggle();
    }
  };

  const noun =
    numberOfGuests === 1 ? rootUnit.noun_singular : rootUnit.noun_plural;
  const unitLabel = capitalize(noun);

  return (
    <HeaderContainer
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
    >
      <TopSection>
        <TourImage src={tourPicture} alt={tourNameTranslation} />
        <ContentSection>
          <TourName>{tourNameTranslation}</TourName>
          <BookingDetails>
            <span>{date}</span>
            <Separator />
            <span>{time}</span>
            <Separator />
            <span>
              {numberOfGuests} {unitLabel}
            </span>
          </BookingDetails>
          <PriceRow>
            <Price>{totalFormatted}</Price>
            <ToggleIcon isExpanded={isExpanded} className="af-angle-down-2" />
          </PriceRow>
        </ContentSection>
      </TopSection>
    </HeaderContainer>
  );
};
